import { create } from 'zustand';
import { TTenantConfig, TTokenData, TUserData } from '@core/shared/types';
import useChatStore from './useChatStore';
import { logger } from '@shared/logger';
import { getBUGroupFromToken, getTenantConfigFromToken } from '@shared/utils';

export type TUserStore = TUserData & {
    tenantConfig: TTenantConfig;
    buGroup: string | null;
    /**
     * Sets the user data and updates the tenant configuration and business unit (BU) group.
     * @param {TUserData} userInfo - The user data object to set.
     * @returns {void}
     */
    setUserData: (userInfo: TUserData) => void;
};

const SCOPE_NAME = 'UserStore'; // Define the scope constant

/**
 * Zustand store for managing user data, including user info, tenant configuration, and business unit (BU) group.
 */
const useUserStore = create<TUserStore>((set) => ({
    preferred_username: '',
    prename: '',
    surname: '',
    name: '',
    tokenData: { groups: [] }, // Ensure `groups` exists to avoid runtime errors
    token: null,
    buGroup: null,
    tenantConfig: {} as TTenantConfig,

    /**
     * Sets the user data, including determining and storing the tenant config and business unit (BU) group.
     * This method also logs the tenant config and BU group.
     * @param {TUserData} userData - The user data object containing user information and token data.
     * @returns {void}
     */
    setUserData: (userData: TUserData): void => {
        // Set loading state for userData
        useChatStore.getState().setLoadingState('userData', false);

        // Retrieve the tenant configuration from the user's token data
        const tenantConfig = getTenantConfigFromToken(userData.tokenData as TTokenData);
        logger.log(SCOPE_NAME, 1, 'got Tenant Config:', tenantConfig);

        // Determine the business unit (BU) group from the token data and tenant config
        const buGroup = getBUGroupFromToken(userData.tokenData as TTokenData, tenantConfig);
        logger.log(SCOPE_NAME, 1, 'got BU Group:', buGroup);

        // Log and save the user data, including the BU group and tenant config
        logger.log(SCOPE_NAME, 1, 'save UserData', {
            ...userData,
            buGroup,
            tenantConfig,
        });

        //split the pre_name and name from the name
        const name = userData.name.split(',');
        userData.prename = name[1];
        userData.surname = name[0];

        set({
            ...userData,
            buGroup,
            tenantConfig,
        });
    },
}));

export default useUserStore;
